import {
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  OnInit,
  EventEmitter,
} from '@angular/core';
import { NavigationOptions } from 'src/app/model/queryParams';
import { EChartsOption } from 'echarts';
import * as echarts from 'echarts';
import { GraphType, chartTypeMap } from 'src/app/model/generic-graph';
import { DataService } from 'src/app/services/data.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sales-var-slide',
  templateUrl: './sales-var-slide.component.html',
  styleUrls: ['./sales-var-slide.component.scss']
})
export class SalesVarSlideComponent implements OnInit {
  @Input() isLoading: boolean = true;
  @Input() currentBUIndex: number = 0;
  @Input() set data(value: any) {
    const shouldResetSlider =
      value?.graph?.colData?.length &&
      this?.data?.graph?.colData?.length &&
      value?.graph?.colData?.length !== this?.data?.graph?.colData?.length;
    this._data = value;
    this.initializeChartOptionsSlider(shouldResetSlider);
  }
  private _data: any = {} as any;
  get data() {
    return this._data;
  }
  @Input() navState: NavigationOptions = {};
  @Input() navInfo: NavigationOptions = {};
  @Input() selectedSliderValue!: any;
  @Output() axisPointerChange = new EventEmitter<any>();
  private _destroyed$ = new Subject();
  public currentPath: any = {};
  public myChartSlider: any;
  public selectedFilter: string = "sales";
  public chartOptionsSlider: EChartsOption = {};
  public formattedColData: any[] = [];
  public baseOptionsSlider: any = {
    grid: {
      left: '4%',
      right: '4%',
      top: '4%',
      bottom: '20%',
      containLabel: true,
    },
    tooltip: {
      triggerOn: 'none',
      show: false,
    },
    xAxis: [
      {
        offset: 10,
        type: 'category',
        boundaryGap: false,
        data: [],
        axisLine: {
          lineStyle: {
            color: '#294E67',
          },
        },
        axisTick: {
          interval: 0,
        },
        axisPointer: {
          animation: true,
          snap: true,
          triggerOn: 'mousemove|click',
          lineStyle: {
            opacity: 0,
          },
          label: {
            show: false,
          },
          handle: {
            show: true,
            margin: -10,
            color: '#004866',
            size: 28,
            shadowBlur: 2,
            shadowColor: '#001E34',
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            icon: 'path://M12 2C6.63 2 2 6.63 2 12s4.63 10 10 10 10-4.63 10-10S17.37 2 12 2z M12 8.5a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7z',
          },
        },
      },
      {
        type: 'category',
        boundaryGap: false,
        axisLine: {
          show: false,
          onZero: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          margin: 0,
          color: '#768B95',
          fontSize: 10,
          fontFamily: 'InterMedium',
          borderColor: '#1A4F69',
          align: 'center',
          borderWidth: 1,
          borderType: 'solid',
          borderRadius: 4,
          backgroundColor: '#002949',
          padding: [1, 4, 2, 4],
          formatter: function (value: any) {
            return value && value;
          },
        },
        position: 'bottom',
        offset: 40,
        data: [],
      },
    ],
    yAxis: {
      type: 'value',
    },
    series: [
      {
        data: [],
        type: 'line',
      },
    ],
  };

  constructor(private dataService: DataService) {}
  public userAxisPointerChange(event: any): void {
    this.axisPointerChange.emit(event);
    this.selectedSliderValue = event;
  }
  initializeChartOptionsSlider(shouldResetSlider = false): void {
    this.formattedColData = this.data.graph.colData.map((item: any) => {
      return item.colName;
    });
    this.baseOptionsSlider.xAxis[0].data = this.formattedColData;
    this.chartOptionsSlider = { ...this.baseOptionsSlider };
    const defaultpointInTime = this.getDefaultPointInTime(
      this.data.graph.colData,
      shouldResetSlider
    );
    this.baseOptionsSlider.xAxis[0].axisPointer.value = defaultpointInTime;
    this.userAxisPointerChange(defaultpointInTime);
  }

  private initChartEventsSlider(chartInstance: any): void {
    let isDragging = false;
    let lastValue: number | null = null;
    const zr = chartInstance.getZr();
    zr.on('mousedown', (event: any) => {
      if (event.target && event.target.type === 'path') {
        event.event.stopPropagation();
        event.event.preventDefault();
      }
    });

    chartInstance.on('updateAxisPointer', (params: any) => {
      if (params.axesInfo && params.axesInfo.length > 0) {
        isDragging = true;
        lastValue = params.axesInfo[0].value;
      }
    });

    chartInstance.getZr().on('mouseup', () => {
      if (isDragging && lastValue !== null) {
        if (lastValue !== this.selectedSliderValue) {
          const timeRangeMap = ['M', 'QTD', 'YTD'];
          const currentSlideMap = ['last year', 'no comparasion', 'budget'];
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'fireEvent',
            event_name: 'element_slide',
            division: this?.currentPath?.div || 'ICL',
            chart_name: 'Sales Waterfall',
            type: 'engagement',
            section: 'chart',
            chart_type: chartTypeMap.get(GraphType.SALES_WATERFALL),
            chart_state: this?.navState?.dropdown ?? undefined,
            time_range: timeRangeMap[this?.navState?.nav || 0],
            comparison_scope:
              currentSlideMap[
                this?.navState?.slide !== undefined ? this?.navState?.slide : 1
              ],
            sub_business_unit:
              this.data?.graph?.data?.[this.navState?.bu || 0]?.title,
            purpose: 'sliding between time periods',
            description: `${lastValue + 1}`,
          });
          this.userAxisPointerChange(lastValue);
        }
        isDragging = false;
        lastValue = null;
      }
    });
  }

  onChartInitSlider(ec: any): void {
    this.myChartSlider = ec;
    this.initChartEventsSlider(ec);
  }

  ngOnInit() {
    this.dataService.currentPath
      .pipe(takeUntil(this._destroyed$))
      .subscribe((params) => {
        this.currentPath = params;
      });
    this.initializeChartOptionsSlider();
  }
  private moveHandleToPoint(index: number): void {
    const newAxisPointerValue = this?.formattedColData[index];

    if (this.myChartSlider) {
      this.myChartSlider.setOption({
        xAxis: [
          {
            type: 'category',
            axisPointer: {
              value: newAxisPointerValue,
            },
          },
        ],
      });
    }
  }
  private getDefaultPointInTime(colData: any[], isDefault = false): number {
    const defaultPoint = colData.findIndex(
      (item: any) => item.isDefault === true
    );
    const pointIndex = defaultPoint !== -1 ? defaultPoint : 0;
    if (isDefault) {
      return pointIndex;
    }
    return colData[this.selectedSliderValue]
      ? this.selectedSliderValue
      : pointIndex;
  }
  
  trackByIndex(index: number, item: any): number {
    return index;
  }
  ngOnChanges(changes: any) {
      if(changes['selectedSliderValue']){
        this.moveHandleToPoint(changes['selectedSliderValue'].currentValue);
      }
    }
  ngOnDestroy(): void {
    this._destroyed$.next(true);
  }
  setByQty(){
    if(this.selectedFilter === "qtyAsc" || this.selectedFilter === "priceDesc"){
      this.selectedFilter = "qtyDesc"
    }else if(this.selectedFilter === "qtyDesc"){
      this.selectedFilter = "sales"
    }else{
      this.selectedFilter="qtyAsc"
    }
  }
  setByPrice(){
    if(this.selectedFilter === "priceAsc" || this.selectedFilter === "qtyDesc"){
      this.selectedFilter = "priceDesc"
    }else if(this.selectedFilter === "priceDesc"){
      this.selectedFilter = "sales"
    }else{
      this.selectedFilter="priceAsc"
    }
  }
  setToUp(){
    if(this.selectedFilter === "priceDesc"){
      this.selectedFilter = "priceAsc"
    }
    if(this.selectedFilter === "qtyDesc"){
      this.selectedFilter = "qtyAsc"
    }
  }
  setToDown(){
    if(this.selectedFilter === "priceAsc"){
      this.selectedFilter = "priceDesc"
    }
    if(this.selectedFilter === "qtyAsc"){
      this.selectedFilter = "qtyDesc"
    }
  }
  setToSales(){
    this.selectedFilter ="sales"
  }
}
